import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { PlanUsageNudge } from './PlanUsageNudge';
import { PricingFaq } from './PricingFaq';
import { PricingIntervalToggle } from './PricingIntervalToggle';
import { PricingPlanGrid } from './PricingPlanGrid';
import { SeeFullComparisonButton } from './SeeFullComparisonButton';
import { PRICING_PLANS, getCurrentLineupIndex } from './pricingPlans.config';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { getUpgradeEntrypoint } from '@/Domain/components/Subscription/upgradeFunnelAnalytics';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { SubscriptionHelper } from '@/Domain/helper/Subscription.helper';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { cn } from '@/Domain/utils/cn';
import { StripeData } from '@/UI/Subscription/Subscription.page';

type Props = {
  stripeData: StripeData;
  isBillingYearly: boolean;
  onBillingYearlyChange: (isBillingYearly: boolean) => void;
};

export const PricingPlans = ({ stripeData, isBillingYearly, onBillingYearlyChange }: Props) => {
  const [openFaq, setOpenFaq] = useState('');
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { capture } = usePostHogAnalytics();

  const { t, i18n } = useTranslation('subscription_page');
  const { currentPlan, subscription } = stripeData;
  const entrypoint = getUpgradeEntrypoint(searchParams);

  const currentIndex = getCurrentLineupIndex(currentPlan.tier);
  const lowerPlans = currentIndex > 0 ? PRICING_PLANS.slice(0, currentIndex) : [];
  const mainPlans = PRICING_PLANS.slice(lowerPlans.length);
  const [showLowerPlans, setShowLowerPlans] = useState(() =>
    lowerPlans.some((plan) => plan.tier === subscription.next_tier),
  );
  const lowerPlanNames = new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(
    lowerPlans.map((plan) => t(`tiers.${plan.key}`)),
  );

  const handleLowerPlansToggle = () => {
    capture(PostHogEventEnum.BillingLowerPlansToggled, {
      entrypoint,
      tier: currentPlan.tier,
      expanded: !showLowerPlans,
    });
    setShowLowerPlans((shown) => !shown);
  };
  const planViewTracked = useRef(false);

  // Mounts only once both subscription queries resolve, so firing means the plans really rendered.
  useEffect(() => {
    if (planViewTracked.current) return;
    planViewTracked.current = true;
    capture(PostHogEventEnum.BillingPlansViewed, {
      entrypoint,
      tier: currentPlan.tier,
      // Not the toggle: the parent sets that in a layout effect, so local state races this.
      current_interval: currentPlan.interval,
      is_free_account: SubscriptionHelper.isFreeAccount(currentPlan.tier),
      payment_failed: subscription.payment_failed,
      subscription_canceled: subscription.canceled,
    });
  }, [capture, entrypoint, currentPlan, subscription]);

  const handleIntervalChange = useCallback(
    (yearly: boolean) => {
      capture(PostHogEventEnum.SubscriptionIntervalToggled, {
        entrypoint,
        tier: currentPlan.tier,
        interval: yearly ? SubscriptionIntervalEnum.Annually : SubscriptionIntervalEnum.Monthly,
      });
      onBillingYearlyChange(yearly);
    },
    [capture, entrypoint, currentPlan.tier, onBillingYearlyChange],
  );

  const handleFaqLink = useCallback((faqKey: string) => {
    setOpenFaq(faqKey);
    requestAnimationFrame(() => {
      document
        .getElementById(`faq-${faqKey}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // Deep-link support: arriving with #faq-<key> (e.g. the "AI credits" links in the
  // sidebar/topbar credits cards) opens and scrolls to that FAQ. Keyed on location.key
  // so it re-fires for same-page clicks too.
  useEffect(() => {
    const match = location.hash.match(/^#faq-([\w-]+)$/);
    if (match) handleFaqLink(match[1]);
  }, [location.key, location.hash, handleFaqLink]);

  return (
    <div className="flex flex-col gap-12">
      <div className="flex flex-col gap-6">
        <PlanUsageNudge currentPlan={currentPlan} />
        <div className="flex justify-start">
          <PricingIntervalToggle
            isBillingYearly={isBillingYearly}
            onChange={handleIntervalChange}
          />
        </div>
        <PricingPlanGrid
          plans={mainPlans}
          isBillingYearly={isBillingYearly}
          stripeData={stripeData}
          onFaqLink={handleFaqLink}
          testId="pricing-main-plans"
        />
        {lowerPlans.length > 0 && (
          <div className="flex flex-col gap-4">
            <div className="flex justify-center">
              <Button
                variant="link"
                size="sm"
                className="h-auto min-h-8 max-w-full whitespace-normal text-center text-muted-foreground"
                aria-expanded={showLowerPlans}
                onClick={handleLowerPlansToggle}
                data-testid="pricing-lower-plans-toggle"
              >
                {showLowerPlans
                  ? t('pricing.lower_plans.hide')
                  : t('pricing.lower_plans.show', { plans: lowerPlanNames })}
                <ChevronDown
                  className={cn('transition-transform', showLowerPlans && 'rotate-180')}
                  aria-hidden="true"
                />
              </Button>
            </div>
            {showLowerPlans && (
              <PricingPlanGrid
                plans={lowerPlans}
                isBillingYearly={isBillingYearly}
                stripeData={stripeData}
                onFaqLink={handleFaqLink}
                testId="pricing-lower-plans"
              />
            )}
          </div>
        )}
      </div>
      <SeeFullComparisonButton />
      <PricingFaq openValue={openFaq} onOpenChange={setOpenFaq} />
    </div>
  );
};
